import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Users,
  FileText,
  Repeat as RepeatIcon,
  ChevronLeft,
  ChevronRight,
  ListChecks,
  Paperclip,
  X as XIcon,
  Lock,
  Camera,
  Pencil,
  Trash2,
} from "lucide-react";
import { api, auth } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { kindOf, eventEmoji, mergeWithBirthdays } from "@/lib/events";
import { eventCountdown } from "@/lib/events";
import { summarizeRrule } from "@/lib/rrule";
import { isTeenPlusFamily } from "@/lib/birthdays";
import ConfettiBurst from "@/components/ConfettiBurst";
import { compressEventAttachmentFile } from "@/lib/eventAttachment";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

/**
 * EventInfoPage — read-only detail view for a single calendar event.
 * Used as the deep-link target from Display mode and anywhere else
 * that wants to surface event detail without opening the full calendar.
 *
 * Hydration: instant when navigated with state, falls back to
 * api.upcomingEvents(80) on refresh / shared link.
 *
 * Visual:
 *   - Header tint matches the event's `kind` colour. Mix is dimmed to
 *     60% paper for teen-plus families and 40% paper for kids — same
 *     ratio used by the calendar grid, so the page reads as part of
 *     the calendar even when reached from elsewhere.
 *   - Prev / next arrows step through chronologically-ordered upcoming
 *     events. Disabled at the ends.
 *   - When the event has a linked todo list, the list shows up as a
 *     card with a one-tap open link.
 */
function fmtFullDateTime(iso, lang = "en") {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const isDa = lang === "da";
  const dateLocale = isDa ? "da-DK" : "en-US";
  const isAllDay = d.getHours() === 0 && d.getMinutes() === 0;
  if (isAllDay) {
    return d.toLocaleDateString(dateLocale, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  // Compose date + time so we can route the time half through fmtTime
  // (which honours the family's Time format setting). toLocaleString's
  // `hour: "2-digit"` would force 12h on en-US locales no matter what.
  const datePart = d.toLocaleDateString(dateLocale, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return isDa ? `${datePart} kl. ${fmtTime(d)}` : `${datePart} · ${fmtTime(d)}`;
}

function fmtRange(startIso, endIso, lang = "en") {
  const a = fmtFullDateTime(startIso, lang);
  if (!endIso) return a;
  const sd = new Date(startIso);
  const ed = new Date(endIso);
  if (Number.isNaN(ed.getTime()) || Number.isNaN(sd.getTime())) return a;
  const sameDay =
    sd.getFullYear() === ed.getFullYear() &&
    sd.getMonth() === ed.getMonth() &&
    sd.getDate() === ed.getDate();
  if (sameDay) {
    const endTime = fmtTime(ed);
    const isAllDay = sd.getHours() === 0 && sd.getMinutes() === 0;
    if (isAllDay) return a;
    return `${a} – ${endTime}`;
  }
  return `${a}\u00A0\u00A0→\u00A0\u00A0${fmtFullDateTime(endIso, lang)}`;
}

export default function EventInfoPage() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(location.state?.event || null);
  const [members, setMembers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [linkedList, setLinkedList] = useState(null);
  const [loading, setLoading] = useState(!location.state?.event);
  // Where the user came from — usually `/app` (Dashboard) but Display
  // Mode passes `/display` so the back arrow returns home to the
  // ambient kitchen screen instead of dropping the user on the
  // dashboard. Defaults to `/app` for direct shared-link visits.
  const backTo = location.state?.from === "/display" ? "/display" : "/app";
  const [error, setError] = useState(null);
  // One-shot confetti burst — fires every time we land on a birthday
  // event (mount and prev/next navigation). Keyed on the event id so
  // ConfettiBurst remounts and replays even when the page itself is
  // reused (no full route remount when next/prev is internal state).
  const [burstKey, setBurstKey] = useState(0);
  // Effective billing tier — controls whether the paperclip "Attach"
  // chip opens the file picker or the BillingSheet upgrade flow.
  const [tier, setTier] = useState("free");
  // Lightbox toggle for tap-to-zoom on the attachment thumbnail.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Pending upload state so the chip can show "Compressing…" while
  // the canvas does its work, then "Saving…" while the PUT is in
  // flight.
  const [attachBusy, setAttachBusy] = useState(false);
  const fileRef = React.useRef(null);

  useEffect(() => {
    let live = true;
    api.billingStatus()
      .then((s) => { if (live) setTier(s?.tier || "free"); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  // Sync `event` state with the route id whenever the URL param
  // changes (prev/next navigation). React Router reuses this same
  // component when only `:id` changes, so the initial useState value
  // is stale after the first navigation. Prefer the state-passed
  // payload (instant, no flicker), then the cached allEvents list,
  // then drop event so the load effect fetches fresh.
  useEffect(() => {
    const passed = location.state?.event;
    if (passed && passed.id === id) {
      setEvent(passed);
      setLoading(false);
      setError(null);
      return;
    }
    const cached = allEvents.find((e) => e.id === id);
    if (cached) {
      setEvent(cached);
      setLoading(false);
      setError(null);
      return;
    }
    setEvent(null);
    setLoading(true);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!auth.getToken()) {
      navigate("/app", { replace: true });
      return undefined;
    }
    let alive = true;
    let loadedMembers = null;
    api.listMembers()
      .then((rows) => {
        if (!alive) return;
        loadedMembers = rows || [];
        setMembers(loadedMembers);
      })
      .catch(() => {/* members are optional decoration */});
    // Always load the upcoming list — needed for prev/next navigation
    // even when state-hydrated. 80 is plenty; calendars rarely have
    // more than a few months ahead loaded at once.
    api.upcomingEvents(80)
      .then(async (rows) => {
        if (!alive) return;
        setAllEvents(rows || []);
        if (!event) {
          // Try real events first, then fall back to merged-with-birthdays
          // so a /event/birthday_<member>_<year> deep-link still hydrates.
          let found = (rows || []).find(
            (e) =>
              e.id === id ||
              (e.master_id &&
                id &&
                id.startsWith(e.master_id) &&
                id.includes("::") &&
                id.split("::")[1] === e.recurrence_original_iso),
          );
          if (!found) {
            // Wait for members if they haven't landed yet.
            if (!loadedMembers) {
              try {
                loadedMembers = await api.listMembers();
                if (alive) setMembers(loadedMembers || []);
              } catch { loadedMembers = []; }
            }
            const merged = mergeWithBirthdays(rows || [], loadedMembers || []);
            found = merged.find((e) => e.id === id);
          }
          if (found) {
            setEvent(found);
          } else {
            setError("Event not found — it may have ended or been deleted.");
          }
        }
      })
      .catch(() => alive && !event && setError("Couldn't load this event."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id, event, navigate]);

  // Fetch the linked list metadata when event has one. We use the
  // family-wide list endpoint and filter — there's no single-list GET
  // and adding one just for this view isn't worth the surface area.
  useEffect(() => {
    let alive = true;
    if (!event?.linked_list_id) {
      setLinkedList(null);
      return () => { alive = false; };
    }
    api.listTodoLists()
      .then((rows) => {
        if (!alive) return;
        const hit = (rows || []).find((l) => l.id === event.linked_list_id);
        setLinkedList(hit || null);
      })
      .catch(() => {/* missing list is fine — just hide the card */});
    return () => { alive = false; };
  }, [event?.linked_list_id]);

  const tagged = useMemo(() => {
    if (!event?.member_ids?.length) return [];
    return event.member_ids
      .map((mid) => members.find((m) => m.id === mid))
      .filter(Boolean);
  }, [event, members]);

  const kind = useMemo(() => kindOf(event?.kind), [event]);
  const recurrenceSummary = useMemo(
    () => (event?.rrule ? summarizeRrule(event.rrule) : null),
    [event],
  );

  // Birthday detection: real `kind == "birthday"` events plus the
  // virtual birthdays synthesised from member birthdays (source ==
  // "birthday"). Either flavour deserves the confetti welcome.
  const isBirthday = useMemo(
    () => event?.kind === "birthday" || event?.source === "birthday",
    [event?.kind, event?.source],
  );

  // Re-fire the burst whenever the visible event id changes — covers
  // mount AND prev/next clicks. The ConfettiBurst component is keyed
  // by `burstKey` so React remounts it and replays the animation.
  useEffect(() => {
    if (event?.id && isBirthday) {
      setBurstKey((k) => k + 1);
    }
  }, [event?.id, isBirthday]);

  // "Also added by Dad" — when more than one parent independently
  // wrote the same event in the same window, the backend silently
  // merged them and stamped `also_added_by_member_ids`. Surface the
  // names quietly so the family knows it wasn't lost.
  const alsoAddedByNames = useMemo(() => {
    const ids = event?.also_added_by_member_ids || [];
    if (!ids.length || !members.length) return [];
    return ids
      .map((mid) => members.find((m) => m.id === mid)?.name)
      .filter(Boolean);
  }, [event, members]);

  // Teen-plus calmer tint, matching CalendarSheet's behaviour (60% paper).
  // Kid families get a warmer, more saturated 40% paper mix.
  const fadeTint = useMemo(() => true, []);

  // Prev / next ids — chronologically ordered. Includes virtual
  // birthdays from the family member list so navigation doesn't
  // dead-end when surrounding events are all synthetic birthdays
  // (which `/api/events/upcoming` doesn't return). Mirrors the
  // calendar + display-mode merging behaviour.
  const { prevId, nextId } = useMemo(() => {
    if (!event) return { prevId: null, nextId: null };
    const merged = mergeWithBirthdays(allEvents, members);
    if (!merged.length) return { prevId: null, nextId: null };
    const sorted = [...merged].sort(
      (a, b) => new Date(a.starts_at) - new Date(b.starts_at),
    );
    const idx = sorted.findIndex((e) => e.id === event.id);
    if (idx === -1) return { prevId: null, nextId: null };
    return {
      prevId: idx > 0 ? sorted[idx - 1].id : null,
      nextId: idx < sorted.length - 1 ? sorted[idx + 1].id : null,
    };
  }, [event, allEvents, members]);

  const goTo = (otherId) => {
    if (!otherId) return;
    const merged = mergeWithBirthdays(allEvents, members);
    const next = merged.find((e) => e.id === otherId);
    // Preserve the `from` flag through prev/next navigation so the
    // back arrow keeps pointing at Display Mode if that's where the
    // user came from, even after sliding through 3 events.
    navigate(`/event/${encodeURIComponent(otherId)}`, {
      state: next ? { event: next, from: location.state?.from } : { from: location.state?.from },
    });
  };

  // Photo attachment — paid-tier feature. The picker / paperclip lives
  // here so anyone in the family can attach a ticket / flyer / receipt
  // after the event has been created (mum schedules the vet visit, dad
  // pins the receipt later from his phone).
  const isVirtualEvent = !!event?.is_recurring_instance || event?.source === "birthday";
  const canAttach = !!event && !isVirtualEvent;
  const openPickerOrUpgrade = () => {
    if (tier === "free") {
      window.dispatchEvent(
        new CustomEvent("famfeel:open-billing", {
          detail: {
            reason: {
              suggested_tier: "plus",
              title: "Photo attachments are part of Plus",
              message: "Tickets, flyers and receipts attach to events on the Plus plan and above.",
            },
          },
        })
      );
      return;
    }
    fileRef.current?.click();
  };

  const onAttachmentFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !event) return;
    setAttachBusy(true);
    try {
      const dataUrl = await compressEventAttachmentFile(f);
      const updated = await api.updateEvent(event.id, { attachment_image: dataUrl });
      setEvent((prev) => (prev ? { ...prev, ...updated } : updated));
      toast.success("Photo attached");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error("Photo attachments need the Plus plan or above.");
      } else {
        toast.error(err?.response?.data?.detail || err?.message || "Couldn't attach photo");
      }
    } finally {
      setAttachBusy(false);
    }
  };

  const removeAttachment = async () => {
    if (!event) return;
    if (!window.confirm("Remove this photo from the event?")) return;
    setAttachBusy(true);
    try {
      const updated = await api.updateEvent(event.id, { attachment_image: "" });
      setEvent((prev) => (prev ? { ...prev, ...updated } : updated));
      toast.success("Photo removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Couldn't remove photo");
    } finally {
      setAttachBusy(false);
    }
  };

  // Viewer parent check — Edit + Delete buttons only render for
  // parents on real (non-virtual) events. Mirrors CalendarSheet's
  // `isParent = !viewer || viewer.role === "parent"` so role-locked
  // kid sessions can still browse but not mutate.
  const isParent = useMemo(() => {
    if (!members.length) return true; // no-members family — parent by default
    const vid = (typeof window !== "undefined" && localStorage.getItem("famfeel_viewer_id")) || null;
    if (!vid) return true;
    const v = members.find((m) => m.id === vid);
    return !v || v.role === "parent";
  }, [members]);
  const isVirtual = !!event && event.source === "birthday";
  // Recurring instances ARE editable/deletable: tapping Edit hands off
  // to the calendar's scope picker ("this / future / all"). Birthdays
  // come from member profiles so they stay locked.
  const canMutate = !!event && isParent && !isVirtual;
  const [deleteBusy, setDeleteBusy] = useState(false);

  const onTapEdit = () => {
    if (!event) return;
    // Send the family back to /app with a state flag — Dashboard
    // detects it, opens the calendar sheet and the inline editor for
    // this event id. Keeps the actual editor (with all its conflict /
    // scope logic) in one place.
    navigate("/app", { state: { calendarOpenEditEventId: event.id } });
  };

  const onTapDelete = async () => {
    if (!event || deleteBusy) return;
    // Recurring instance → hand off to the calendar's scope picker
    // ("this / future / all") instead of deleting directly. The
    // calendar already owns that flow; we just route the user there.
    if (event.is_recurring_instance) {
      navigate("/app", { state: { calendarOpenEditEventId: event.id, calendarRequestDelete: true } });
      return;
    }
    if (!window.confirm(`Delete "${event.title}"?`)) return;
    setDeleteBusy(true);
    try {
      await api.deleteEvent(event.id);
      toast.success("Event deleted");
      // Navigate back to where the user came from so the freshly-
      // deleted item disappears from view (Display Mode → /display,
      // Dashboard → /app). reuses the existing scrollTo flag pointing
      // at the deleted id is harmless (CalendarSheet just doesn't
      // find it).
      navigate(backTo);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Couldn't delete event";
      toast.error(detail);
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[color:var(--bg)] flex items-center justify-center px-6">
        <div className="label-tiny text-[color:var(--ink-3)]">Loading event…</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-[color:var(--bg)] flex items-center justify-center px-6">
        <div className="max-w-md text-center" data-testid="event-info-missing">
          <h1 className="font-display-italic text-3xl mb-3">
            {error || "Event not found"}
          </h1>
          <Link to="/app" className="ff-btn ff-btn-primary inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            Back to FamFeel
          </Link>
        </div>
      </div>
    );
  }

  const tint = kind?.tint || "var(--bg-soft)";
  const headerBg = fadeTint
    ? `color-mix(in srgb, var(--bg) 50%, ${tint})`
    : `color-mix(in srgb, var(--bg) 40%, ${tint})`;

  return (
    <div
      className="min-h-screen bg-[color:var(--bg)] text-[color:var(--ink)]"
      data-testid="event-info-page"
    >
      <header
        className="px-5 md:px-10 py-5 md:py-8 relative overflow-hidden"
        style={{ background: headerBg }}
      >
        {isBirthday && (
          <ConfettiBurst
            key={burstKey}
            duration={1800}
            data-testid="event-info-confetti"
          />
        )}
        <div className="max-w-3xl mx-auto">
          {/* Header: back link + day-slider (prev event ‹ date label ›
              next event). Mirrors the dashboard's Today/day switcher
              shape so the navigation feels consistent across screens.
              Edit + Delete moved out of the header into the bottom
              action group so the most-tapped buttons sit near the
              thumbs on phones. */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <Link
              to={backTo}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-[color:var(--line)] bg-[color:var(--bg)]/70 hover:bg-[color:var(--bg)] transition-colors"
              aria-label={backTo === "/display" ? "Back to Display mode" : "Back to FamFeel"}
              title={backTo === "/display" ? "Back to Display mode" : "Back to FamFeel"}
              data-testid="event-info-back"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
            </Link>
            {(() => {
              // Day-slider: prev/next chevrons flank a friendly date
              // label of the *current* event. Tapping the chevrons
              // navigates to the previous / next event in the
              // chronological list (same behaviour as before — only
              // the chrome changed).
              const sd = event.starts_at ? new Date(event.starts_at) : null;
              const todayKey = (() => {
                const t = new Date();
                return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
              })();
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              const tomorrowKey = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
              let label = t({ en: "Event", da: "Begivenhed" });
              if (sd && !Number.isNaN(sd.getTime())) {
                const evKey = `${sd.getFullYear()}-${String(sd.getMonth() + 1).padStart(2, "0")}-${String(sd.getDate()).padStart(2, "0")}`;
                if (evKey === todayKey) label = t({ en: "Today", da: "I dag" });
                else if (evKey === tomorrowKey) label = t({ en: "Tomorrow", da: "I morgen" });
                else label = sd.toLocaleDateString(lang === "da" ? "da-DK" : "en-US", { weekday: "short", day: "numeric", month: "short" });
              }
              return (
                <div
                  className="flex items-center gap-1 min-w-0"
                  data-testid="event-info-slider"
                >
                  <button
                    type="button"
                    onClick={() => goTo(prevId)}
                    disabled={!prevId}
                    className="w-9 h-9 flex-shrink-0 inline-flex items-center justify-center rounded-full border border-[color:var(--line)] bg-[color:var(--bg)]/70 hover:bg-[color:var(--bg)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous event"
                    data-testid="event-info-prev"
                  >
                    <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                  </button>
                  <span
                    className="px-2 font-display text-base leading-none tracking-tight whitespace-nowrap text-[color:var(--ink)] min-w-[64px] text-center"
                    data-testid="event-info-slider-label"
                  >
                    {label}
                  </span>
                  <button
                    type="button"
                    onClick={() => goTo(nextId)}
                    disabled={!nextId}
                    className="w-9 h-9 flex-shrink-0 inline-flex items-center justify-center rounded-full border border-[color:var(--line)] bg-[color:var(--bg)]/70 hover:bg-[color:var(--bg)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next event"
                    data-testid="event-info-next"
                  >
                    <ChevronRight className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              );
            })()}
          </div>
          <div className="flex items-start gap-4">
            <div
              className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[color:var(--bg)] border border-[color:var(--surface)] flex items-center justify-center text-3xl md:text-4xl leading-none"
              data-testid="event-info-emoji"
            >
              {eventEmoji(event)}
            </div>
            <div className="flex-1 min-w-0">
              <h1
                className="font-display-italic text-3xl md:text-5xl tracking-tight leading-tight"
                data-testid="event-info-title"
              >
                {event.title}
              </h1>
              {alsoAddedByNames.length > 0 && (
                <div
                  className="label-tiny text-[color:var(--ink-3)] mt-2"
                  data-testid="event-info-also-added-by"
                >
                  Also added by{" "}
                  {alsoAddedByNames.length === 1
                    ? alsoAddedByNames[0]
                    : alsoAddedByNames.length === 2
                      ? `${alsoAddedByNames[0]} and ${alsoAddedByNames[1]}`
                      : `${alsoAddedByNames.slice(0, -1).join(", ")} and ${alsoAddedByNames.slice(-1)[0]}`}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="px-5 md:px-10 py-8 max-w-3xl mx-auto space-y-6">
        <Section icon={CalendarDays} label="When" testid="event-info-when">
          <div className="text-lg leading-snug whitespace-pre-line">
            {fmtRange(event.starts_at, event.ends_at, lang)}
          </div>
          {/* Live "Happening now" badge — matches Display mode + the
              dashboard event-row treatment. Shows only while the
              current time sits between start and end (so a finished
              event quietly drops the badge). End-time is intentionally
              omitted here because the WHEN line just above already
              shows the full "09:00 – 14:30" range — duplicating it in
              the badge was visual noise. */}
          {eventCountdown(event.starts_at, event.ends_at) === "ongoing" && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.06em] font-semibold px-2.5 py-1 rounded-full bg-[color:var(--accent)] text-[#fcfcfa]"
              data-testid="event-info-happening-now"
              title={event.ends_at ? `Ends ${fmtTime(event.ends_at)}` : "In progress"}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-current"
                aria-hidden="true"
              />
              Happening now
            </div>
          )}
          {recurrenceSummary && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-[13px] text-[color:var(--ink-2)]"
              data-testid="event-info-rrule"
            >
              <RepeatIcon className="w-3.5 h-3.5" strokeWidth={2} />
              {recurrenceSummary}
            </div>
          )}
        </Section>

        {tagged.length > 0 && (
          <Section icon={Users} label="Who's going" testid="event-info-attendees">
            <div className="flex flex-wrap gap-2">
              {tagged.map((m) => (
                <div
                  key={m.id}
                  className="inline-flex items-center gap-2 rounded-full bg-[color:var(--bg-soft)] px-3 py-1.5 text-[14px]"
                  data-testid={`event-info-attendee-${m.id}`}
                >
                  <span className="text-lg leading-none">{m.avatar_emoji || "🙂"}</span>
                  <span>{m.name}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {event.location && (
          <Section icon={MapPin} label="Where" testid="event-info-location">
            <div className="text-base leading-snug whitespace-pre-line">{event.location}</div>
          </Section>
        )}

        {event.notes && (
          <Section icon={FileText} label="Notes" testid="event-info-notes">
            <div className="text-base leading-relaxed whitespace-pre-wrap">{event.notes}</div>
          </Section>
        )}

        {canAttach && (event.attachment_image ? (
          <Section icon={Paperclip} label="Photo" testid="event-info-attachment">
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="block ff-card p-2 hover:opacity-90 transition-opacity"
              data-testid="event-info-attachment-open"
            >
              <img
                src={event.attachment_image}
                alt="Event attachment"
                className="block max-w-full max-h-72 rounded-lg object-contain"
              />
            </button>
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={openPickerOrUpgrade}
                disabled={attachBusy}
                className="ff-chip inline-flex items-center gap-1.5"
                data-testid="event-info-attachment-replace"
              >
                <Camera className="w-3.5 h-3.5" strokeWidth={1.75} />
                {attachBusy ? "Working…" : "Replace"}
              </button>
              <button
                type="button"
                onClick={removeAttachment}
                disabled={attachBusy}
                className="ff-chip inline-flex items-center gap-1.5"
                data-testid="event-info-attachment-remove"
              >
                <XIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
                Remove
              </button>
            </div>
          </Section>
        ) : (
          <Section icon={Paperclip} label="Photo" testid="event-info-attachment-empty">
            <button
              type="button"
              onClick={openPickerOrUpgrade}
              disabled={attachBusy}
              className={`ff-chip inline-flex items-center gap-1.5 ${tier === "free" ? "opacity-70" : ""}`}
              data-testid="event-info-attachment-add"
            >
              {tier === "free" ? (
                <Lock className="w-3.5 h-3.5" strokeWidth={1.75} />
              ) : (
                <Paperclip className="w-3.5 h-3.5" strokeWidth={1.75} />
              )}
              {attachBusy
                ? t({ en: "Compressing…", da: "Komprimerer…" })
                : t({ en: "Attach photo", da: "Vedhæft foto" })}
              {tier === "free" && (
                <span className="ml-1 text-[10px] tracking-wider uppercase text-[color:var(--ink-3)]">
                  Plus
                </span>
              )}
            </button>
          </Section>
        ))}

        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          onChange={onAttachmentFile}
          className="hidden"
          data-testid="event-info-attachment-input"
        />

        {linkedList && (
          <Section icon={ListChecks} label="Linked list" testid="event-info-list">
            <Link
              to="/app"
              state={{ openListId: linkedList.id }}
              className="ff-card p-4 flex items-center gap-3 hover:bg-[color:var(--bg-soft)] transition-colors"
              data-testid={`event-info-list-${linkedList.id}`}
            >
              <span className="text-2xl leading-none flex-shrink-0" aria-hidden="true">
                📝
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[15px] font-display leading-tight tracking-tight truncate">
                  {linkedList.title}
                </div>
                <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5">
                  {linkedList.item_count === 0
                    ? "Empty list"
                    : linkedList.done_count >= linkedList.item_count
                      ? `All ${linkedList.item_count} done ✨`
                      : `${linkedList.item_count - linkedList.done_count} of ${linkedList.item_count} left`}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[color:var(--ink-3)]" strokeWidth={2} />
            </Link>
          </Section>
        )}

        {/* Bottom action group — Edit, Delete, "Open in calendar" all
            sit together in the page footer. Phone-first: most-tapped
            buttons stay in thumb reach instead of in the top-right
            header. Edit + Delete only render for parents on real
            (non-virtual) events; the open-in-calendar link always
            renders since even kids may want to scroll back to context.
            All three buttons use `ff-chip` so they match the smaller
            "Attach photo" chip above — the page reads as one
            consistent control size, and Open-in-calendar still leads
            visually thanks to the active fill. */}
        <div className="pt-4 flex flex-wrap items-center gap-2">
          <Link
            to="/app"
            state={{ calendarScrollToEventId: event.id }}
            className="ff-chip is-active inline-flex items-center gap-1.5"
            data-testid="event-info-open-calendar"
          >
            <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} />
            Open in calendar
          </Link>
          {canMutate && (
            <>
              <button
                type="button"
                onClick={onTapEdit}
                className="ff-chip inline-flex items-center gap-1.5"
                aria-label="Edit event"
                data-testid="event-info-edit"
              >
                <Pencil className="w-3.5 h-3.5" strokeWidth={2} />
                Edit
              </button>
              <button
                type="button"
                onClick={onTapDelete}
                disabled={deleteBusy}
                className="ff-chip inline-flex items-center gap-1.5 text-[color:var(--rough-strong)] disabled:opacity-50"
                aria-label="Delete event"
                data-testid="event-info-delete"
              >
                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                Delete
              </button>
            </>
          )}
        </div>
      </main>

      {lightboxOpen && event?.attachment_image && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setLightboxOpen(false)}
          data-testid="event-info-attachment-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white inline-flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="event-info-attachment-lightbox-close"
          >
            <XIcon className="w-5 h-5" strokeWidth={2} />
          </button>
          <img
            src={event.attachment_image}
            alt="Event attachment"
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, label, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="label-tiny text-[color:var(--ink-3)] inline-flex items-center gap-1.5 mb-2">
        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
        {label}
      </div>
      <div className="text-[color:var(--ink)]">{children}</div>
    </section>
  );
}
